import axios, { AxiosInstance } from 'axios';
import { wrapper } from 'axios-cookiejar-support';
import { CookieJar } from 'tough-cookie';
import { authUserSignal } from '../signals/authSignal';
import { createLogger } from '../utils/logger';

const logger = createLogger('HTTPService');

// Vite exposes env vars via import.meta.env (must be prefixed with VITE_)
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4500/api';

// require:
// npm install axios tough-cookie axios-cookiejar-support
export class HTTPService {
  private httpClient: AxiosInstance;
  // Set lazily via setAuthService() to avoid a circular import/instantiation
  // dependency between HTTPService <-> AuthService.
  private authService: { refreshLogin: () => Promise<boolean> } | undefined;

  constructor() {
    logger.debug('baseURL=', BASE_URL);

    this.httpClient = wrapper(
      axios.create({
        jar: new CookieJar(),
        withCredentials: true,
        baseURL: BASE_URL,
      })
    );

    this.httpClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        logger.debug(
          'interceptor in response status=',
          error.response?.status,
          'url=',
          error.config?.url
        );
        const prevRequest = error?.config;

        if (
          error?.response?.status === 403 &&
          !prevRequest.sent &&
          prevRequest.url.indexOf('refresh_token') > 0
        ) {
          prevRequest.sent = true;
          logger.debug('httpClient callback for refresh_token expired on backend');
          authUserSignal.value = undefined;
          window.location.reload();
        } else if (error?.response?.status === 403 && !prevRequest.sent) {
          logger.debug('trying refreshLogin(), url=', prevRequest.url);
          prevRequest.sent = true; // prevents infinite loop for 403 error codes
          if (this.authService) {
            await this.authService.refreshLogin();
          }
          return this.httpClient(prevRequest);
        } else if (error?.response?.status === 401) {
          // Stored credentials are no longer valid. Clearing the signal alone does not
          // redirect anywhere: RequiredAuth only reads authUserSignal.value at its own
          // render time (plain @preact/signals, no React re-render subscription), so it
          // won't react to this mutation unless something else causes it to re-render.
          // Force navigation to /login directly, as the logout flow and the
          // refresh-token-expired branch above both already do.
          logger.debug('httpClient callback for 401, redirecting to /login');
          authUserSignal.value = undefined;
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Wire up the AuthService instance after both have been constructed.
   * This breaks the circular dependency between HTTPService and AuthService.
   */
  public setAuthService(authService: { refreshLogin: () => Promise<boolean> }) {
    this.authService = authService;
  }

  public getClient() {
    return this.httpClient;
  }

  public get<T = any>(url: string, config?: any) {
    return this.httpClient.get<T>(url, config);
  }

  public post<T = any>(url: string, data?: any, config?: any) {
    return this.httpClient.post<T>(url, data, config);
  }

  public put<T = any>(url: string, data?: any, config?: any) {
    return this.httpClient.put<T>(url, data, config);
  }

  public patch<T = any>(url: string, data?: any, config?: any) {
    return this.httpClient.patch<T>(url, data, config);
  }

  public delete<T = any>(url: string, config?: any) {
    return this.httpClient.delete<T>(url, config);
  }
}
